import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, ArrowRightLeft } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useTableSort, SortableTh } from "../../components/tableSort";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtKg = (n) => (n ?? 0).toLocaleString("it-IT", { maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "—");

const CAUSALE_BADGE = {
  CAR: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100",
  TRC: "bg-teal-100 text-teal-800 hover:bg-teal-100",
  TRS: "bg-purple-100 text-purple-800 hover:bg-purple-100",
  PAT: "bg-red-100 text-red-800 hover:bg-red-100",
  VEN: "bg-blue-100 text-blue-800 hover:bg-blue-100",
};
const CAUSALE_LABEL = {
  CAR: "Carico fornitore", TRC: "Carico produzione", TRS: "Trasformazione", PAT: "Scarto", VEN: "Vendita",
};

export default function WmsMovimenti() {
  const [data, setData] = useState({ movements: [], totals: null });
  const [loading, setLoading] = useState(true);
  const [causale, setCausale] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const sort = useTableSort();

  const load = useCallback(async () => {
    try {
      const params = {};
      if (causale !== "all") params.causale = causale;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (search) params.search = search;
      const r = await axios.get(`${API}/wms/movements`, { params });
      setData(r.data);
    } catch { toast.error("Errore caricamento movimenti"); }
    finally { setLoading(false); }
  }, [causale, dateFrom, dateTo, search]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const sorted = sort.sortList(data.movements, { counterpart: (m) => m.customer_name || m.supplier_name || "" });

  return (
    <div className="space-y-4" data-testid="wms-movimenti-page">
      <div>
        <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2"><ArrowRightLeft className="w-6 h-6 text-[#8A1C2A]" />WMS — Movimenti</h1>
        <p className="text-sm text-stone-500">Giornale completo: carichi, trasformazioni, scarti e vendite (da Tracciabilità Etifood)</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2 items-end">
        <div>
          <Label className="text-xs text-stone-500">Tipo movimento</Label>
          <Select value={causale} onValueChange={setCausale}>
            <SelectTrigger data-testid="wms-mov-causale"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i movimenti</SelectItem>
              <SelectItem value="CAR">📦 Carico fornitore</SelectItem>
              <SelectItem value="TRC">🏭 Carico produzione</SelectItem>
              <SelectItem value="TRS">🔪 Trasformazione</SelectItem>
              <SelectItem value="PAT">🗑️ Scarto lavorazione</SelectItem>
              <SelectItem value="VEN">🛒 Vendita</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-stone-500">Dal</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="wms-mov-from" />
        </div>
        <div>
          <Label className="text-xs text-stone-500">Al</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="wms-mov-to" />
        </div>
        <div className="col-span-2">
          <Label className="text-xs text-stone-500">Cerca</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Partita, articolo, cliente, fornitore, lotto…" className="pl-9" data-testid="wms-mov-search" />
          </div>
        </div>
      </div>

      {data.totals && (
        <div className="flex flex-wrap gap-4 text-sm text-stone-600" data-testid="wms-mov-totals">
          <span><b>{data.totals.count}</b> movimenti</span>
          <span className="text-emerald-700">+ <b>{fmtKg(data.totals.kg_in)}</b> kg caricati</span>
          <span className="text-red-700">− <b>{fmtKg(data.totals.kg_out)}</b> kg scaricati</span>
        </div>
      )}

      <Card className="bg-white border-stone-200 overflow-hidden">
        <div className="overflow-x-auto max-h-[62vh] overflow-y-auto">
          <table className="w-full text-sm min-w-[1020px]">
            <thead className="bg-stone-50 border-b border-stone-200 sticky top-0 z-10">
              <tr>
                <SortableTh label="Data" sortId="date" sort={sort} />
                <SortableTh label="Tipo" sortId="causale" sort={sort} />
                <SortableTh label="N." sortId="num_mov" sort={sort} align="right" />
                <SortableTh label="Articolo" sortId="product_code" sort={sort} />
                <SortableTh label="Descrizione" sortId="description" sort={sort} />
                <SortableTh label="Partita" sortId="partita" sort={sort} />
                <SortableTh label="Lotto" sortId="lot" sort={sort} />
                <SortableTh label="Cliente / Fornitore" sortId="counterpart" sort={sort} />
                <SortableTh label="Kg +" sortId="qty_in" sort={sort} align="right" />
                <SortableTh label="Kg −" sortId="qty_out" sort={sort} align="right" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="10" className="p-8 text-center text-stone-500">Caricamento…</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan="10" className="p-8 text-center text-stone-500" data-testid="wms-mov-empty">Nessun movimento — carica l'export «Tracciabilità» dalla pagina Giacenze</td></tr>
              ) : sorted.map((m) => (
                <tr key={m.id || m.import_key} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`wms-mov-row-${m.import_key?.slice(0, 8)}`}>
                  <td className="p-2.5 whitespace-nowrap">{fmtDate(m.date)}</td>
                  <td className="p-2.5"><Badge className={CAUSALE_BADGE[m.causale]}>{CAUSALE_LABEL[m.causale] || m.movement}</Badge></td>
                  <td className="p-2.5 text-right font-mono text-xs">{m.num_mov}</td>
                  <td className="p-2.5 font-mono text-xs">{m.product_code}</td>
                  <td className="p-2.5 truncate max-w-[220px]">{m.description}</td>
                  <td className="p-2.5 font-mono text-xs text-[#8A1C2A]">{m.partita}</td>
                  <td className="p-2.5 font-mono text-xs">{m.lot || "—"}</td>
                  <td className="p-2.5 truncate max-w-[180px] text-xs">{m.customer_name || (m.supplier_name && m.supplier_name !== "PRODUZIONE INTERNA" ? m.supplier_name : m.supplier_name === "PRODUZIONE INTERNA" ? "Produzione interna" : "—")}</td>
                  <td className="p-2.5 text-right text-emerald-700 font-semibold">{m.qty_in > 0 ? fmtKg(m.qty_in) : ""}</td>
                  <td className="p-2.5 text-right text-red-700 font-semibold">{m.qty_out > 0 ? fmtKg(m.qty_out) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
